import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Send, Users, Building2, Mail, Phone, Clock, CheckCircle2, Star,
  Copy, Search, RefreshCw, Key, X, UserCheck, AlertCircle, User,
  Link2, Tag, ChevronDown, Upload, FileSpreadsheet, Check, XCircle, Loader2,
  ArrowUpDown, ArrowUp, ArrowDown, Download, FileText, Info, ShieldCheck,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  invite_sent: { label: 'Invite Sent', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Send },
  activated:   { label: 'Activated',   color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
};

const PLAN_COLORS = {
  Executive: 'bg-amber-100 text-amber-800 border-amber-200',
  Premier:   'bg-purple-100 text-purple-800 border-purple-200',
  Essential: 'bg-blue-100 text-blue-800 border-blue-200',
  Hybrid:    'bg-slate-100 text-slate-700 border-slate-200',
};

const AVAILABLE_ROLES = [
  'General Member', 'Advisor', 'Ambassador', 'Board Member',
  'Committee Member', 'Counselor', 'Contributor', 'Liaison', 'Student', 'Sponsor', 'Partner', 'Volunteer',
];

const MembersManagementPage = () => {
  const [members, setMembers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all'); // 'all', 'business', 'individual'
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState({});
  const [tempAccessModal, setTempAccessModal] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [rolesModal, setRolesModal] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importUploading, setImportUploading] = useState(false);
  const [importConfirming, setImportConfirming] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = React.useRef(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ name: '', email: '', company_name: '', membership_type: 'hybrid', is_founding_member: true, phone: '' });
  const [addingMember, setAddingMember] = useState(false);
  const [sort, setSort] = useState(null); // { id, direction }

  // Lock body scroll while ANY modal is open
  useBodyScrollLock(
    !!tempAccessModal || !!inviteResult || !!linkModal || !!rolesModal || !!showAddMember
  );

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, companiesRes] = await Promise.all([
        axios.get(`${API}/admin/pending-members`, { withCredentials: true }),
        axios.get(`${API}/admin/business-members`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setMembers(membersRes.data);
      setCompanies(companiesRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSendInvite = async (member) => {
    setSending(prev => ({ ...prev, [member.id]: 'invite' }));
    try {
      const res = await axios.post(`${API}/admin/send-invite/${member.id}`, {}, { withCredentials: true });
      setInviteResult({ member, url: res.data.activation_url, token: res.data.invite_token });
      toast.success(`Invitation sent to ${member.email}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(prev => ({ ...prev, [member.id]: null })); }
  };

  const handleTempAccess = async () => {
    if (!tempAccessModal || !tempPassword || tempPassword.length < 6) { toast.error('Min 6 characters'); return; }
    setSending(prev => ({ ...prev, [tempAccessModal.id]: 'temp' }));
    try {
      const res = await axios.post(`${API}/admin/temporary-access/${tempAccessModal.id}`, { password: tempPassword }, { withCredentials: true });
      toast.success(`Access created for ${tempAccessModal.name}`);
      setInviteResult({ member: tempAccessModal, tempAccess: true, email: res.data.email, password: res.data.temp_password });
      setTempAccessModal(null); setTempPassword('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(prev => ({ ...prev, [tempAccessModal?.id]: null })); }
  };

  const handleMarkActivated = async (member) => {
    setSending(prev => ({ ...prev, [member.id]: 'mark' }));
    try {
      await axios.post(`${API}/admin/mark-activated/${member.id}`, {}, { withCredentials: true });
      toast.success(`${member.name} activated`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(prev => ({ ...prev, [member.id]: null })); }
  };

  const handleLinkCompany = async (companyUserId) => {
    if (!linkModal) return;
    try {
      const res = await axios.put(`${API}/admin/members/${linkModal.id}/link-company`, { company_user_id: companyUserId }, { withCredentials: true });
      toast.success(res.data.message);
      setLinkModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUnlinkCompany = async (member) => {
    try {
      await axios.put(`${API}/admin/members/${member.id}/link-company`, { company_user_id: "" }, { withCredentials: true });
      toast.success('Company link removed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSaveRoles = async () => {
    if (!rolesModal) return;
    try {
      await axios.put(`${API}/admin/members/${rolesModal.id}/roles`, { roles: selectedRoles }, { withCredentials: true });
      toast.success('Roles updated');
      setRolesModal(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copied'));

  // Data filtering
  const businessMembers = members.filter(m => !m.is_individual && m.company_name);
  const individualMembers = members.filter(m => m.is_individual || !m.company_name);
  const displayMembers = tab === 'business' ? businessMembers : tab === 'individual' ? individualMembers : members;

  const filtered = displayMembers.filter(m => {
    if (statusFilter !== 'all' && m.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (m.name || '').toLowerCase().includes(s) || (m.email || '').toLowerCase().includes(s) || (m.company_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  // Sort accessors for each column id
  const SORT_ACCESSORS = {
    type: (m) => (!m.is_individual && m.company_name) ? 'a_business' : 'b_individual',
    name: (m) => ((!m.is_individual && m.company_name) ? m.company_name : m.name || '').toLowerCase(),
    email: (m) => (m.email || '').toLowerCase(),
    plan: (m) => (m.plan_name || '').toLowerCase(),
    status: (m) => m.status || '',
  };

  const sortedFiltered = React.useMemo(() => {
    if (!sort?.id) return filtered;
    const acc = SORT_ACCESSORS[sort.id];
    if (!acc) return filtered;
    const dir = sort.direction === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = acc(a), bv = acc(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir * String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [filtered, sort]);  

  const toggleSort = (id) => {
    setSort((prev) => {
      if (prev?.id !== id) return { id, direction: 'asc' };
      if (prev.direction === 'asc') return { id, direction: 'desc' };
      return null;
    });
  };

  const SortIcon = ({ id }) => {
    if (sort?.id !== id) return <ArrowUpDown className="w-3 h-3 opacity-40 group-hover:opacity-70 inline-block ml-1" />;
    return sort.direction === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#002249] inline-block ml-1" />
      : <ArrowDown className="w-3 h-3 text-[#002249] inline-block ml-1" />;
  };

  const counts = { all: members.length, pending: 0, invite_sent: 0, activated: 0 };
  members.forEach(m => { counts[m.status] = (counts[m.status] || 0) + 1; });

  const getLinkedCompanyName = (companyId) => {
    const c = companies.find(cp => cp.user_id === companyId);
    return c?.company_name || '';
  };

  const filteredCompanies = companies.filter(c => {
    if (!linkSearch) return true;
    return c.company_name.toLowerCase().includes(linkSearch.toLowerCase());
  });

  const handleImportUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportUploading(true);
    setImportPreview(null);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/members/import/preview`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
      toast.success(`Parsed ${res.data.summary.total_rows} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setImportUploading(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  const toggleImportSkip = (idx) => {
    setImportPreview(prev => {
      const updated = { ...prev, preview: [...prev.preview] };
      updated.preview[idx] = { ...updated.preview[idx], skip: !updated.preview[idx].skip };
      const toImport = updated.preview.filter(p => !p.skip && !p.errors?.length).length;
      updated.summary = { ...updated.summary, to_import: toImport };
      return updated;
    });
  };

  const handleConfirmImport = async () => {
    if (!importPreview) return;
    const entries = importPreview.preview; // send ALL rows; backend honors .skip + re-verifies
    if (entries.length === 0) { toast.error('No entries to import'); return; }
    setImportConfirming(true);
    try {
      const res = await axios.post(`${API}/admin/members/import/confirm`, { entries }, { withCredentials: true });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.created} members${res.data.skipped_duplicates ? ` · ${res.data.skipped_duplicates} duplicates blocked` : ''}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImportConfirming(false); }
  };

  const downloadImportReport = () => {
    if (!importResult && !importPreview) return;
    const rows = [['Category', 'Full Name', 'Email', 'Company', 'Reason / Matched record']];
    if (importResult) {
      (importResult.created_members || []).forEach(m =>
        rows.push(['Created', m.name || '', m.email || '', m.company || '', `${m.membership || ''}${m.founding ? ' · founding' : ''}`])
      );
      (importResult.skipped_rows || []).forEach(r => {
        const matched = r.matched_existing_record;
        const match = matched ? `matches ${matched.name || matched.email || matched.id || ''}` : '';
        rows.push([r.reason?.startsWith('duplicate_') ? 'Duplicate' : r.reason === 'admin_skipped' ? 'Skipped' : 'Error',
                   r.full_name || '', r.email || '', '', `${r.reason || ''}${match ? ' — ' + match : ''}`]);
      });
    } else if (importPreview) {
      importPreview.preview.forEach(p => {
        const matched = p.matched_existing_record;
        const match = matched ? `matches ${matched.name || matched.email || ''}` : '';
        rows.push([
          p.classification === 'duplicate' ? 'Duplicate' :
            p.classification === 'error' ? 'Error' :
            p.classification === 'missing_info' ? 'Missing info' : 'New',
          p.full_name || '', p.email || '', p.company_name || '',
          [p.errors?.join('; '), p.matched_by ? `matched by ${p.matched_by}` : '', match].filter(Boolean).join(' — '),
        ]);
      });
    }
    const csv = rows.map(r => r.map(c => {
      const v = String(c ?? '');
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateSlug = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `member-import-report-${dateSlug}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addMemberForm.email || !addMemberForm.name) { toast.error('Name and email are required'); return; }
    setAddingMember(true);
    try {
      await axios.post(`${API}/admin/members/add`, addMemberForm, { withCredentials: true });
      toast.success(`Member ${addMemberForm.name} added successfully`);
      setAddMemberForm({ name: '', email: '', company_name: '', membership_type: 'hybrid', is_founding_member: true, phone: '' });
      setShowAddMember(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally { setAddingMember(false); }
  };

  const toggleFeatured = async (company) => {
    const profileId = company.id || company.user_id;
    const newVal = !company.is_featured_homepage;
    try {
      await axios.put(`${API}/admin/company-profiles/${profileId}/featured`, { is_featured_homepage: newVal }, { withCredentials: true });
      toast.success(newVal ? 'Added to homepage' : 'Removed from homepage');
      fetchData();
    } catch (err) { toast.error('Failed to update'); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="members-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Member Activation Center</h1>
          <p className="text-slate-600 text-sm mt-1">
            {businessMembers.length} businesses &middot; {individualMembers.length} individuals &middot; {counts.activated} activated
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setShowAddMember(!showAddMember); setShowImport(false); }}
            className={`rounded-sm text-sm ${showAddMember ? 'bg-[#002249] text-white' : ''}`}
            variant={showAddMember ? 'default' : 'outline'} data-testid="toggle-add-member">
            <UserCheck className="w-4 h-4 mr-2" /> Add Member
          </Button>
          <Button onClick={() => { setShowImport(!showImport); setShowAddMember(false); setImportPreview(null); setImportResult(null); }}
            className={`rounded-sm text-sm ${showImport ? 'bg-[#002249] text-white' : ''}`}
            variant={showImport ? 'default' : 'outline'} data-testid="toggle-import">
            <Upload className="w-4 h-4 mr-2" /> Import Members
          </Button>
          <Button onClick={fetchData} variant="outline" className="rounded-sm" data-testid="refresh-members">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* ═══ ADD MEMBER FORM ═══ */}
      <AnimatePresence>
        {showAddMember && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mb-6 overflow-hidden" data-testid="add-member-section">
            <form onSubmit={handleAddMember} className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <UserCheck className="w-5 h-5 text-[#002249]" />
                <h2 className="font-bold text-[#002249]">Add New Member</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Full Name *</label>
                  <input value={addMemberForm.name} onChange={e => setAddMemberForm({ ...addMemberForm, name: e.target.value })}
                    className="w-full border border-slate-200 rounded-sm p-2 text-sm" placeholder="John Doe" required data-testid="add-member-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                  <input type="email" value={addMemberForm.email} onChange={e => setAddMemberForm({ ...addMemberForm, email: e.target.value })}
                    className="w-full border border-slate-200 rounded-sm p-2 text-sm" placeholder="email@company.com" required data-testid="add-member-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Company Name <span className="text-slate-400">(leave empty for individual)</span></label>
                  <input value={addMemberForm.company_name} onChange={e => setAddMemberForm({ ...addMemberForm, company_name: e.target.value })}
                    className="w-full border border-slate-200 rounded-sm p-2 text-sm" placeholder="Company LLC" data-testid="add-member-company" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Membership Type</label>
                  <select value={addMemberForm.membership_type} onChange={e => setAddMemberForm({ ...addMemberForm, membership_type: e.target.value })}
                    className="w-full border border-slate-200 rounded-sm p-2 text-sm" data-testid="add-member-type">
                    <option value="hybrid">Hybrid</option>
                    <option value="essential">Essential</option>
                    <option value="premier">Premier</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                  <input value={addMemberForm.phone} onChange={e => setAddMemberForm({ ...addMemberForm, phone: e.target.value })}
                    className="w-full border border-slate-200 rounded-sm p-2 text-sm" placeholder="(555) 123-4567" data-testid="add-member-phone" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={addMemberForm.is_founding_member} onChange={e => setAddMemberForm({ ...addMemberForm, is_founding_member: e.target.checked })} className="rounded" />
                    <span className="text-sm text-slate-600">Founding Member</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddMember(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" disabled={addingMember} className="rounded-sm bg-[#002249] hover:bg-[#001833] text-white" data-testid="submit-add-member">
                  {addingMember ? 'Adding...' : 'Add Member'}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ IMPORT SECTION ═══ */}
      <AnimatePresence>
        {showImport && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mb-6 overflow-hidden" data-testid="import-section">
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-[#002249]" />
                  <h2 className="font-bold text-[#002249]">Import Members</h2>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Duplicate protection active
                </div>
              </div>

              {/* Upload area */}
              {!importPreview && !importResult && (
                <div className="border-2 border-dashed border-slate-200 rounded-sm p-8 text-center hover:border-[#002249]/30 transition-colors">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-700 font-medium mb-1">Upload a member list</p>
                  <p className="text-xs text-slate-500 mb-1">CSV · XLSX (Excel) · PDF</p>
                  <p className="text-[11px] text-slate-400 mb-4">
                    Expected columns: Full Name, Email, Phone, Company Name, Membership Type, Address, Category, Notes
                  </p>
                  <input type="file" ref={importFileRef} onChange={handleImportUpload} accept=".csv,.pdf,.xlsx" className="hidden" data-testid="import-file-input" />
                  <Button onClick={() => importFileRef.current?.click()} disabled={importUploading} className="rounded-sm bg-[#002249] hover:bg-[#001833] text-white" data-testid="upload-import-file">
                    {importUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing...</> : <><Upload className="w-4 h-4 mr-2" /> Choose File</>}
                  </Button>
                  <p className="text-[11px] text-slate-400 mt-4 max-w-md mx-auto leading-relaxed">
                    The system checks <strong>email</strong>, <strong>phone</strong>, <strong>full name</strong>, and <strong>company name</strong> against existing records before importing. Active members are never deleted or overwritten.
                  </p>
                </div>
              )}

              {/* Preview table */}
              {importPreview && !importResult && (
                <div>
                  {/* Summary — 5-category classification */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="import-summary-grid">
                    {[
                      { key: 'total_rows', label: 'Total Rows', value: importPreview.summary.total_rows, color: 'text-slate-800', bg: 'bg-slate-50', ring: 'ring-slate-200' },
                      { key: 'new', label: 'New', value: importPreview.summary.new, color: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
                      { key: 'duplicates', label: 'Duplicates', value: importPreview.summary.duplicates, color: 'text-amber-700', bg: 'bg-amber-50', ring: 'ring-amber-200' },
                      { key: 'missing_info', label: 'Missing Info', value: importPreview.summary.missing_info, color: 'text-sky-700', bg: 'bg-sky-50', ring: 'ring-sky-200' },
                      { key: 'errors', label: 'Errors', value: importPreview.summary.errors, color: 'text-rose-700', bg: 'bg-rose-50', ring: 'ring-rose-200' },
                    ].map(s => (
                      <div key={s.key} className={`${s.bg} rounded-sm p-3 text-center ring-1 ${s.ring}`} data-testid={`summary-${s.key}`}>
                        <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <p className="text-[11px] text-slate-500 mb-3 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span>
                      Duplicates and errors are auto-unchecked and will NOT be imported.
                      You can manually toggle any row — the backend will still re-verify and block
                      any duplicate before inserting.
                    </span>
                  </p>

                  {/* Table */}
                  <div className="border border-slate-200 rounded-sm overflow-x-auto max-h-[440px] overflow-y-auto" data-testid="import-preview-table">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="p-2 text-left font-medium text-slate-500 w-10">Import</th>
                          <th className="p-2 text-left font-medium text-slate-500">Name</th>
                          <th className="p-2 text-left font-medium text-slate-500">Email</th>
                          <th className="p-2 text-left font-medium text-slate-500 hidden md:table-cell">Phone</th>
                          <th className="p-2 text-left font-medium text-slate-500">Company</th>
                          <th className="p-2 text-left font-medium text-slate-500">Plan</th>
                          <th className="p-2 text-left font-medium text-slate-500">Status</th>
                          <th className="p-2 text-left font-medium text-slate-500 hidden lg:table-cell">Matches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.preview.map((row, idx) => {
                          const cls = row.classification || 'new';
                          const rowBg =
                            cls === 'error' ? 'bg-rose-50/60' :
                            cls === 'duplicate' ? 'bg-amber-50/60' :
                            cls === 'missing_info' ? 'bg-sky-50/40' : '';
                          const canToggle = cls !== 'error';
                          return (
                            <tr key={idx} className={`border-t border-slate-100 ${rowBg} ${row.skip ? 'opacity-70' : ''}`} data-testid={`import-row-${idx}`} data-classification={cls}>
                              <td className="p-2">
                                <button onClick={() => canToggle && toggleImportSkip(idx)} disabled={!canToggle}
                                  className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${!canToggle ? 'cursor-not-allowed opacity-50' : ''}`}
                                  style={{ borderColor: row.skip ? '#cbd5e1' : '#002249', backgroundColor: row.skip ? 'transparent' : '#002249' }}
                                  data-testid={`import-toggle-${idx}`} title={!canToggle ? 'Cannot import — missing required fields' : ''}>
                                  {!row.skip && <Check className="w-3 h-3 text-white" />}
                                </button>
                              </td>
                              <td className="p-2 font-medium text-slate-800 max-w-[180px] truncate">{row.full_name || <span className="text-slate-400 italic">missing</span>}</td>
                              <td className="p-2 text-slate-600 max-w-[180px] truncate">{row.email || <span className="text-slate-400 italic">missing</span>}</td>
                              <td className="p-2 text-slate-600 hidden md:table-cell">{row.phone || <span className="text-slate-300">—</span>}</td>
                              <td className="p-2">
                                {row.company_name ? (
                                  <span className="inline-flex items-center gap-1 text-blue-700 max-w-[140px] truncate"><Building2 className="w-3 h-3 flex-shrink-0" />{row.company_name}</span>
                                ) : <span className="text-slate-400">Individual</span>}
                              </td>
                              <td className="p-2">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${
                                  row.membership_type === 'executive' ? 'bg-amber-100 text-amber-800' :
                                  row.membership_type === 'premier' ? 'bg-purple-100 text-purple-800' :
                                  row.membership_type === 'essential' ? 'bg-blue-100 text-blue-800' :
                                  'bg-slate-100 text-slate-700'
                                }`}>{row.membership_type}</span>
                              </td>
                              <td className="p-2">
                                {cls === 'error' ? (
                                  <span className="inline-flex items-center gap-1 text-rose-700 font-medium"><XCircle className="w-3 h-3" />{row.errors?.[0] || 'Error'}</span>
                                ) : cls === 'duplicate' ? (
                                  <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid={`dup-badge-${idx}`}>
                                    <AlertCircle className="w-3 h-3" />
                                    Duplicate{row.matched_by ? ` · ${row.matched_by.replace('_in_file', ' (in file)')}` : ''}
                                  </span>
                                ) : cls === 'missing_info' ? (
                                  <span className="inline-flex items-center gap-1 text-sky-700"><Info className="w-3 h-3" />Missing info</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><Check className="w-3 h-3" />Ready</span>
                                )}
                              </td>
                              <td className="p-2 hidden lg:table-cell">
                                {row.matched_existing_record ? (
                                  <div className="text-[11px] leading-tight" data-testid={`matched-${idx}`}>
                                    <p className="font-medium text-slate-700 truncate max-w-[180px]">{row.matched_existing_record.name || '—'}</p>
                                    <p className="text-slate-500 truncate max-w-[180px]">{row.matched_existing_record.email || row.matched_existing_record.company || ''}</p>
                                  </div>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={() => { setImportPreview(null); }} className="rounded-sm" data-testid="cancel-import">
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                      <Button variant="outline" onClick={downloadImportReport} className="rounded-sm" data-testid="download-preview-report">
                        <Download className="w-4 h-4 mr-2" /> Download Preview Report
                      </Button>
                    </div>
                    <Button onClick={handleConfirmImport} disabled={importConfirming || importPreview.summary.to_import === 0}
                      className="rounded-sm bg-[#002249] hover:bg-[#001630] text-white" data-testid="confirm-import">
                      {importConfirming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                        : <><CheckCircle2 className="w-4 h-4 mr-2" /> Import {importPreview.summary.to_import} Member{importPreview.summary.to_import === 1 ? '' : 's'}</>}
                    </Button>
                  </div>
                </div>
              )}

              {/* Import result */}
              {importResult && (
                <div className="space-y-4" data-testid="import-result">
                  <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-bold text-emerald-800">Import Complete</p>
                      <p className="text-sm text-emerald-700 mt-0.5">
                        {importResult.created} created · {importResult.skipped_duplicates || 0} duplicates blocked · {importResult.skipped_errors || 0} errors
                      </p>
                    </div>
                  </div>

                  {/* Result stat tiles */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { k: 'total', label: 'Scanned', value: importResult.total || 0, color: 'text-slate-800', bg: 'bg-slate-50', ring: 'ring-slate-200' },
                      { k: 'created', label: 'Created', value: importResult.created || 0, color: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
                      { k: 'dup', label: 'Duplicates', value: importResult.skipped_duplicates || 0, color: 'text-amber-700', bg: 'bg-amber-50', ring: 'ring-amber-200' },
                      { k: 'err', label: 'Errors', value: importResult.skipped_errors || 0, color: 'text-rose-700', bg: 'bg-rose-50', ring: 'ring-rose-200' },
                    ].map(s => (
                      <div key={s.k} className={`${s.bg} rounded-sm p-3 text-center ring-1 ${s.ring}`} data-testid={`result-${s.k}`}>
                        <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {importResult.skipped_rows?.length > 0 && (
                    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 max-h-48 overflow-y-auto">
                      <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-2">Skipped ({importResult.skipped_rows.length})</p>
                      <ul className="space-y-1 text-xs">
                        {importResult.skipped_rows.slice(0, 50).map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                              r.reason?.startsWith('duplicate_') ? 'bg-amber-400' :
                              r.reason?.startsWith('insert_error') ? 'bg-rose-400' : 'bg-slate-400'
                            }`} />
                            <span className="truncate">
                              <strong className="text-slate-800">{r.full_name || r.email || '—'}</strong>
                              <span className="text-slate-400"> · </span>
                              <span className="text-slate-500">{r.reason}</span>
                              {r.matched_existing_record && (
                                <em className="text-slate-400 not-italic"> — matches {r.matched_existing_record.name || r.matched_existing_record.email}</em>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResult.errors?.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                      <p className="text-xs font-bold text-amber-800 mb-1">Details:</p>
                      {importResult.errors.slice(0, 20).map((e, i) => <p key={i} className="text-xs text-amber-700">{e}</p>)}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Button variant="outline" onClick={downloadImportReport} className="rounded-sm" data-testid="download-result-report">
                      <Download className="w-4 h-4 mr-2" /> Download Report (CSV)
                    </Button>
                    <Button variant="outline" onClick={() => { setImportResult(null); setImportPreview(null); setShowImport(false); }} className="rounded-sm" data-testid="close-import-result">
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Businesses', value: businessMembers.length, icon: Building2, color: 'bg-[#002249]' },
          { label: 'Individuals', value: individualMembers.length, icon: User, color: 'bg-indigo-500' },
          { label: 'Pending', value: counts.pending, icon: Clock, color: 'bg-amber-500' },
          { label: 'Activated', value: counts.activated, icon: CheckCircle2, color: 'bg-green-500' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
            <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <div><p className="text-2xl font-bold text-[#002249]">{s.value}</p><p className="text-xs text-slate-500">{s.label}</p></div>
          </div>
        ))}
      </div>

      {/* Type Tabs + Status Filters + Search */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          {/* Type tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="type-tabs">
            {[
              { key: 'all', label: 'All', icon: Users },
              { key: 'business', label: 'Businesses', icon: Building2 },
              { key: 'individual', label: 'Individuals', icon: User },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tab === t.key ? 'bg-white text-[#002249] shadow-sm' : 'text-slate-500'}`}
                data-testid={`tab-${t.key}`}>
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>
          {/* Status filters */}
          <div className="flex gap-1" data-testid="status-filters">
            {['all', 'pending', 'invite_sent', 'activated'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all border ${statusFilter === f ? 'bg-[#002249] text-white border-[#002249]' : 'bg-white text-slate-500 border-slate-200'}`}
                data-testid={`status-${f}`}>
                {f === 'all' ? 'All' : f === 'invite_sent' ? 'Invited' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 rounded-sm text-sm h-8" data-testid="search-members" />
          </div>
        </div>
      </div>

      {/* Members Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : sortedFiltered.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white border border-slate-200 rounded-sm">
          <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium">No members found</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="members-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th onClick={() => toggleSort('type')} className="group cursor-pointer select-none text-left p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:bg-slate-100 transition-colors" data-testid="mm-sort-type">Type<SortIcon id="type" /></th>
                  <th onClick={() => toggleSort('name')} className="group cursor-pointer select-none text-left p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:bg-slate-100 transition-colors" data-testid="mm-sort-name">Name / Company<SortIcon id="name" /></th>
                  <th onClick={() => toggleSort('email')} className="group cursor-pointer select-none text-left p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell hover:bg-slate-100 transition-colors" data-testid="mm-sort-email">Email<SortIcon id="email" /></th>
                  <th onClick={() => toggleSort('plan')} className="group cursor-pointer select-none text-left p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:bg-slate-100 transition-colors" data-testid="mm-sort-plan">Plan / Role<SortIcon id="plan" /></th>
                  <th onClick={() => toggleSort('status')} className="group cursor-pointer select-none text-left p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:bg-slate-100 transition-colors" data-testid="mm-sort-status">Status<SortIcon id="status" /></th>
                  <th className="text-right p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.map((member, index) => {
                  const isBiz = !member.is_individual && !!member.company_name;
                  const statusCfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.pending;
                  const planColor = PLAN_COLORS[member.plan_name] || '';
                  const isBusy = !!sending[member.id];
                  const linkedName = member.company_id ? getLinkedCompanyName(member.company_id) : '';
                  const roles = member.roles?.length > 0 ? member.roles : (member.chamber_role ? [member.chamber_role.replace('_', ' ')] : []);

                  return (
                    <tr key={member.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-testid={`member-row-${index}`}>
                      {/* Type */}
                      <td className="p-3">
                        {isBiz ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-[#002249]/10 text-[#002249] px-2 py-0.5 rounded-sm"><Building2 className="w-3 h-3" />Biz</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-sm"><User className="w-3 h-3" />Ind</span>
                        )}
                      </td>
                      {/* Name */}
                      <td className="p-3">
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-[#002249] truncate">{isBiz ? member.company_name : member.name}</p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isBiz && <p className="text-[11px] text-slate-500 truncate">{member.name}</p>}
                            {!isBiz && linkedName && (
                              <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex items-center gap-0.5 truncate"><Link2 className="w-2.5 h-2.5" />{linkedName}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Email */}
                      <td className="p-3 hidden md:table-cell"><span className="text-[12px] text-slate-600">{member.email}</span></td>
                      {/* Plan / Roles */}
                      <td className="p-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {isBiz && member.plan_name && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${planColor}`}>{member.plan_name}</span>}
                          {!isBiz && roles.map((r, i) => (
                            <span key={i} className="text-[10px] font-medium bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-sm capitalize">{r}</span>
                          ))}
                          {member.is_founding_member && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        </div>
                      </td>
                      {/* Status */}
                      <td className="p-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm border flex items-center gap-1 w-fit ${statusCfg.color}`}>
                          <statusCfg.icon className="w-3 h-3" />{statusCfg.label}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {isBiz && (
                            <Button size="sm" variant="ghost" onClick={() => toggleFeatured(member)} title={member.is_featured_homepage ? 'Remove from homepage' : 'Feature on homepage'}
                              className={`rounded-sm h-7 px-1.5 ${member.is_featured_homepage ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`} data-testid={`toggle-featured-${index}`}>
                              <Star className={`w-3.5 h-3.5 ${member.is_featured_homepage ? 'fill-amber-500' : ''}`} />
                            </Button>
                          )}
                          {member.status !== 'activated' && (
                            <>
                              <Button size="sm" onClick={() => handleSendInvite(member)} disabled={isBusy}
                                className="rounded-sm text-[10px] bg-[#E63946] hover:bg-[#d32f3f] text-white h-7 px-2" data-testid={`send-invite-${index}`}>
                                <Send className="w-3 h-3 mr-1" />{member.invite_sent ? 'Resend' : 'Invite'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setTempAccessModal(member); setTempPassword(''); }} disabled={isBusy}
                                className="rounded-sm text-[10px] h-7 px-2 border-slate-200" data-testid={`temp-access-${index}`}>
                                <Key className="w-3 h-3 mr-1" />Temp
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleMarkActivated(member)} disabled={isBusy}
                                className="rounded-sm h-7 px-1.5 text-slate-400 hover:text-green-600" title="Mark Activated" data-testid={`mark-activated-${index}`}>
                                <UserCheck className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                          {member.status === 'activated' && <span className="text-[10px] text-green-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Active</span>}
                          {/* Individual-specific actions */}
                          {!isBiz && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => { setLinkModal(member); setLinkSearch(''); }} className="rounded-sm h-7 px-1.5 text-slate-400 hover:text-indigo-600" title="Link to Company" data-testid={`link-company-${index}`}>
                                <Link2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setRolesModal(member); setSelectedRoles(member.roles?.length ? [...member.roles] : []); }}
                                className="rounded-sm h-7 px-1.5 text-slate-400 hover:text-purple-600" title="Manage Roles" data-testid={`manage-roles-${index}`}>
                                <Tag className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Temporary Access Modal */}
      <AnimatePresence>
        <ModalPortal>
          {tempAccessModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setTempAccessModal(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="temp-access-modal">
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-[#002249]">Temporary Access</h2>
                    <button onClick={() => setTempAccessModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{tempAccessModal.company_name || tempAccessModal.name}</p>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">Sets a temp password and activates immediately. Member must change password on first login.</p>
                  </div>
                  <div><label className="text-xs font-semibold text-slate-700 block mb-1">Email</label><div className="text-sm text-slate-600 bg-slate-50 p-2.5 rounded-sm border border-slate-200">{tempAccessModal.email}</div></div>
                  <div><label className="text-xs font-semibold text-slate-700 block mb-1">Temporary Password *</label><Input value={tempPassword} onChange={e => setTempPassword(e.target.value)} placeholder="Min 6 characters" className="rounded-sm" data-testid="temp-password-input" /></div>
                </div>
                <div className="p-5 border-t border-slate-200 flex gap-3">
                  <Button variant="outline" onClick={() => setTempAccessModal(null)} className="flex-1 rounded-sm">Cancel</Button>
                  <Button onClick={handleTempAccess} disabled={!tempPassword || tempPassword.length < 6} className="flex-1 bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="confirm-temp-access">
                    <Key className="w-4 h-4 mr-2" />Create Access
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </ModalPortal>
      </AnimatePresence>

      {/* Invite Result Modal */}
      <AnimatePresence>
        <ModalPortal>
          {inviteResult && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setInviteResult(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="invite-result-modal">
                <div className="p-5 border-b border-slate-200 bg-green-50">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                    <div><h2 className="text-lg font-bold text-green-800">{inviteResult.tempAccess ? 'Access Created' : 'Invitation Sent'}</h2><p className="text-sm text-green-700">{inviteResult.member.company_name || inviteResult.member.name}</p></div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  {inviteResult.tempAccess ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between"><div><p className="text-[10px] text-slate-500 uppercase font-bold">Email</p><p className="text-sm font-mono">{inviteResult.email}</p></div><button onClick={() => copyToClipboard(inviteResult.email)}><Copy className="w-3.5 h-3.5 text-slate-400" /></button></div>
                      <div className="flex items-center justify-between"><div><p className="text-[10px] text-slate-500 uppercase font-bold">Password</p><p className="text-sm font-mono text-[#E63946] font-bold">{inviteResult.password}</p></div><button onClick={() => copyToClipboard(inviteResult.password)}><Copy className="w-3.5 h-3.5 text-slate-400" /></button></div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Activation Link</p>
                      <div className="flex items-center gap-2"><p className="text-xs font-mono truncate flex-1">{inviteResult.url}</p><button onClick={() => copyToClipboard(inviteResult.url)}><Copy className="w-3.5 h-3.5 text-slate-400" /></button></div>
                    </div>
                  )}
                </div>
                <div className="p-5 border-t border-slate-200"><Button onClick={() => setInviteResult(null)} className="w-full bg-[#002249] rounded-sm">Done</Button></div>
              </motion.div>
            </div>
          )}
        </ModalPortal>
      </AnimatePresence>

      {/* Link to Company Modal */}
      <AnimatePresence>
        <ModalPortal>
          {linkModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setLinkModal(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="link-company-modal">
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div><h2 className="text-lg font-bold text-[#002249]">Link to Company</h2><p className="text-sm text-slate-500">{linkModal.name}</p></div>
                    <button onClick={() => setLinkModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
                  </div>
                  {linkModal.company_id && (
                    <div className="mt-3 flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <span className="text-sm text-indigo-700 font-medium">Currently linked to: {getLinkedCompanyName(linkModal.company_id)}</span>
                      <Button size="sm" variant="ghost" onClick={() => { handleUnlinkCompany(linkModal); setLinkModal(null); }} className="text-red-500 text-xs">Unlink</Button>
                    </div>
                  )}
                  <div className="mt-3 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={linkSearch} onChange={e => setLinkSearch(e.target.value)} placeholder="Search companies..." className="pl-9 rounded-sm text-sm" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-1">
                  {filteredCompanies.map(c => (
                    <button key={c.user_id} onClick={() => { handleLinkCompany(c.user_id); setLinkModal(null); }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-left transition-colors"
                      data-testid={`link-option-${c.user_id}`}>
                      <div className="w-8 h-8 bg-[#002249] rounded-lg flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0">
                        {c.company_name.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-[#002249]">{c.company_name}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          )}
        </ModalPortal>
      </AnimatePresence>

      {/* Roles Management Modal */}
      <AnimatePresence>
        <ModalPortal>
          {rolesModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRolesModal(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="roles-modal">
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div><h2 className="text-lg font-bold text-[#002249]">Manage Roles</h2><p className="text-sm text-slate-500">{rolesModal.name}</p></div>
                    <button onClick={() => setRolesModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-xs text-slate-500 mb-3">Select one or more roles for this member:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_ROLES.map(role => {
                      const isActive = selectedRoles.includes(role);
                      return (
                        <button key={role} onClick={() => setSelectedRoles(prev => isActive ? prev.filter(r => r !== role) : [...prev, role])}
                          className={`p-2.5 rounded-lg text-xs font-medium text-left transition-all border ${isActive ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                          data-testid={`role-${role.toLowerCase().replace(/\s+/g, '-')}`}>
                          {role}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="p-5 border-t border-slate-200 flex gap-3">
                  <Button variant="outline" onClick={() => setRolesModal(null)} className="flex-1 rounded-sm">Cancel</Button>
                  <Button onClick={handleSaveRoles} className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-sm" data-testid="save-roles-btn">
                    <Tag className="w-4 h-4 mr-2" />Save Roles ({selectedRoles.length})
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </ModalPortal>
      </AnimatePresence>
    </div>
  );
};

export default MembersManagementPage;
